import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BroadcastIncidentScreen } from '@core/models/broadcast-incident-screen.model';
import { FileModel } from '@core/models/file.model';
import {
  CampaignAbattement,
  ChannelLastOpenedDate,
  JsonCampaignAbattement,
  JsonChannelLastOpenedDate,
  JsonScreenStatus,
  JsonScreenType,
  JsonTvScreenHistory,
  JsonTvScreenSpot,
  Screen,
  ScreenStatus,
  ScreenType,
  TvScreen,
  TvScreenHistory,
  TvScreenSpot,
} from '@core/models/screen/screen';
import { AuthService } from '@core/services/auth.service';
import { BaseService } from '@core/services/base/base.service';
import { environment } from '@env/environment';
import moment from 'moment/moment';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ErrorHandlerService, HttpError } from '../error-handler.service';

export interface ScreenData {
  screenChannelCode: string;
  screenChannelRegionCode: string;
  screenStartDate: string; //Format : YYYY-MM-DD
  screenHour: string;
  screenStatus: string;
  shouldAlsoShowIdSpots?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ScreenService extends BaseService {
  public screenStatus: ScreenStatus[] = [];
  public screenType: ScreenType[] = [];
  public spotsByScreen: TvScreenSpot[] = [];
  public screenHistory: TvScreenHistory[] = [];
  private isLoading = new Subject<boolean>();
  public isLoading$ = this.isLoading.asObservable();
  public reloadList = new Subject<boolean>();

  private updateTotalScreen = new Subject<{ screen: Screen; mode: boolean }>();
  public updateTotalScreen$ = this.updateTotalScreen.asObservable();

  private updateScreenHour = new Subject<{}>();
  public updateScreenHour$ = this.updateScreenHour.asObservable();

  public getScreensData = new Subject<{}>();
  public getScreensData$ = this.getScreensData.asObservable();

  private getScreensPriceLib = new Subject<{}>();
  public getScreensPriceLib$ = this.getScreensPriceLib.asObservable();

  private routePlanScreens = `${environment.api_base_url}/tv/plan-screens`;
  private routeScreens = `${environment.api_base_url}/tv/screens`;
  private routeScreenStatus = `${environment.api_base_url}/tv/screen-status`;
  private routeScreenPriceLib = `${environment.api_base_url}/tv/screen-price-lib`;
  private routeScreenDurcalMax = `${environment.api_base_url}/tv/screen-durcalmax`;
  private routeScreenTypes = `${environment.api_base_url}/tv/screen-types`;
  private routeAddScreens = `${environment.api_base_url}/tv/add-new-screens`;
  private routeUpdateScreens = `${environment.api_base_url}/tv/update-screens`;
  private routeDeleteScreens = `${environment.api_base_url}/tv/delete-screens`;
  private routeTransfertRegToNatScreens = `${environment.api_base_url}/tv/transfer-reg-to-nat`;
  private routeSpotsByScreen = `${environment.api_base_url}/tv/get-spots-screen`;
  private routeScreenHistory = `${environment.api_base_url}/tv/get-screen-history`;
  private routeLastOpenedScreenDate = `${environment.api_base_url}/tv/last-opened-screen-date`;
  private routeAbattementCampaigns = `${environment.api_base_url}/tv/campaigns-abattements`;
  private routeProductSector = `${environment.api_base_url}/tv/product-sector`;
  private routeExportScreenGrid = `${environment.api_base_url}/tv/export-screen-grid`;
  private routeExportTypeWeek = `${environment.api_base_url}/tv/export-type-week`;
  private routeDownloadScreenGrid = `${environment.api_base_url}/tv/download-export-screen-grid`;

  constructor(private http: HttpClient, private errorHandlerService: ErrorHandlerService, private authService: AuthService) {
    super();
  }

  // read last theme for the user
  getThemeSettings(): Observable<string> {
    const userId = this.authService.currentUserData ? this.authService.currentUserData.sub : '';
    let url = `${environment.api_base_url}/tv/tv-user-plan-theme/${userId}`;
    return new Observable(observer => {
      this.http
        .get(url)
        .pipe(
          map((response: []) => {
            return response['theme']
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            observer.error(error);
          }
        )
    });
  }

  // write current theme for the user
  patchThemeSettings(theme: string): Observable<string> {
    const userId = this.authService.currentUserData ? this.authService.currentUserData.sub : '';
    let url = `${environment.api_base_url}/tv/tv-user-plan-theme/${userId}`;
    return new Observable<any>(observer => {
      this.http
        .patch(url, 
        {
          userId: userId.toUpperCase(),
          theme: theme
        })
        .pipe(
          map((response: []) => {
            return response['theme']
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            observer.error(error);
          }
        )
    });
  }

  /**
   * Get plan screens according to filter value
   * @param screenFilter (param)
   */
  getPlanScreens(screenFilter: object): Observable<object> {
    const apiBaseUrl = `${this.routePlanScreens}`;
    const params: HttpParams = this.buildParamsApi(screenFilter);

    return this.http.get(apiBaseUrl, { params }).pipe(catchError((error: Error) => throwError(error)));
  }

  /**
   * Get screens according to filter value
   * @param screenFilter (param)
   */
  getScreens(screenFilter: object): Observable<TvScreen[]> {
    const apiBaseUrl = `${this.routeScreens}`;
    const params: HttpParams = this.buildParamsApi(screenFilter);
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: 'Erreur lors de la récupération des écrans. Veuillez essayer plus tard.',
      },
    ];

    return new Observable<TvScreen[]>(observer => {
      this.http
        .get(apiBaseUrl, { params })
        .pipe(
          map((response: any) => {
            if (response && response.length > 0) {
              return response.map(screen => new TvScreen(screen));
            }
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  /**
   * Get screens status (ouvert, fermé, inactif, ...)
   * @returns
   */
  getScreenStatus(): Observable<ScreenStatus[]> {
    const apiBaseUrl = `${this.routeScreenStatus}`;
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: 'Erreur lors de la récupération des status écrans. Veuillez essayer plus tard.',
      },
    ];

    return new Observable<ScreenStatus[]>(observer => {
      this.http
        .get(apiBaseUrl, {})
        .pipe(
          map((response: []) => {
            return (this.screenStatus = response.map((status: JsonScreenStatus) => new ScreenStatus(status)));
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  /**
   * Get screen price lib (secteur 1, 2, 3, captif, ...)
   * @param channelId
   * @returns
   */
  getScreenPriceLib(channelParams: object): Observable<[]> {
    const apiBaseUrl = `${this.routeScreenPriceLib}`;
    const params: HttpParams = this.buildParamsApi(channelParams);
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: 'Erreur lors de la récupération des libéllés de tarif. Veuillez essayer plus tard.',
      },
    ];

    return new Observable<[]>(observer => {
      this.http
        .get(apiBaseUrl, { params })
        .pipe(map((screenPriceLib: []) => screenPriceLib))
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  sendScreenPriceLib(screenPriceLib: []): void {
    this.getScreensPriceLib.next(screenPriceLib);
  }

  /**
   * Get screens types (sport, so garanty, ...)
   * @returns
   */
  getScreenTypes(): Observable<ScreenType[]> {
    const apiBaseUrl = `${this.routeScreenTypes}`;
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: "Erreur lors de la récupération des typages d'écran. Veuillez essayer plus tard.",
      },
    ];

    return new Observable<ScreenType[]>(observer => {
      this.http
        .get(apiBaseUrl, {})
        .pipe(
          map((response: []) => {
            return (this.screenType = response.map((type: JsonScreenType) => new ScreenType(type)));
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  /**
   * Get screen durcalmax
   * @param screensInfo
   * @returns
   */
  getScreenDurCalMax(screensInfo: object): Observable<any> {
    const apiBaseUrl = `${this.routeScreenDurcalMax}`;
    const params: HttpParams = this.buildParamsApi(screensInfo);
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: "Erreur lors de la récupération du calibre max de l'écran. Veuillez essayer plus tard.",
      },
    ];

    return new Observable<any>(observer => {
      this.http
        .get(apiBaseUrl, { params })
        .pipe(map(screenDurCalMax => screenDurCalMax))
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  /**
   * Add new screens
   * @param screens object to save
   */
  postScreen(screens: object): Observable<any> {
    const apiBaseUrl = this.routeAddScreens;
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: "Une erreur est survenue lors de la création d'écran. Veuillez essayer plus tard.",
      },
    ];

    return new Observable<any>(observer => {
      this.http
        .post(apiBaseUrl, screens)
        .pipe(map((screenResponse: object) => screenResponse))
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  /**
   * Update screens
   * @param screens object to save
   */
  updateScreen(screens: Array<{}>): Observable<any> {
    const apiBaseUrl = this.routeUpdateScreens;
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: "Une erreur est survenue lors de la modification d'écran. Veuillez essayer plus tard.",
      },
    ];

    return new Observable<any>(observer => {
      this.http
        .put(apiBaseUrl, screens)
        .pipe(map((screenResponse: object) => screenResponse))
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  /**
   * @param screens
   */
  deleteScreens(screens: Array<{}>): Observable<any> {
    const apiBaseUrl = this.routeDeleteScreens;
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: "Une erreur est survenue lors de la suppression d'écran. Veuillez essayer plus tard.",
      },
    ];

    return new Observable<any>(observer => {
      this.http
        .put(apiBaseUrl, screens)
        .pipe(map((screenResponse: object) => screenResponse))
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  /**
   * Transfer regional screen to national screen
   * @param screens object to save
   */
  transferRegToNatScreen(screens: object): Observable<any> {
    const apiBaseUrl = this.routeTransfertRegToNatScreens;
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: "Une erreur est survenue lors du transfert d'écran. Veuillez essayer plus tard.",
      },
    ];

    return new Observable<any>(observer => {
      this.http
        .post(apiBaseUrl, screens)
        .pipe(map((screenResponse: object) => screenResponse))
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  /**
   * Get spots of one screen
   * @returns
   */
  getSpotsByScreen(screen: ScreenData): Observable<TvScreenSpot[]> {
    const apiBaseUrl = `${this.routeSpotsByScreen}`;
    const params: HttpParams = this.buildParamsApi(screen);
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: "Erreur lors de la récupération des spots de l'écran. Veuillez essayer plus tard.",
      },
    ];

    return new Observable<TvScreenSpot[]>(observer => {
      this.http
        .get(apiBaseUrl, { params })
        .pipe(
          map((response: []) => {
            return (this.spotsByScreen = response.map((spot: JsonTvScreenSpot) => new TvScreenSpot(spot)));
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  /**
   * Get screen history
   * @returns
   */
  getScreenHistory(screen: object): Observable<TvScreenHistory[]> {
    const apiBaseUrl = `${this.routeScreenHistory}`;
    const params: HttpParams = this.buildParamsApi(screen);
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: "Erreur lors de la récupération des mouvements de l'écran. Veuillez essayer plus tard.",
      },
    ];

    return new Observable<TvScreenHistory[]>(observer => {
      this.http
        .get(apiBaseUrl, { params })
        .pipe(
          map((response: []) => {
            return (this.screenHistory = response.map((spot: JsonTvScreenHistory) => new TvScreenHistory(spot)));
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  /**
   * Get last opened screen date for each channel
   * @param channels (param)
   */
  getLastOpenedScreenDateByChannels(channels: string): Observable<ChannelLastOpenedDate[]> {
    const apiBaseUrl = `${this.routeLastOpenedScreenDate}`;
    const params: HttpParams = this.buildParamsApi({ channels });

    return this.http
      .get(apiBaseUrl, { params })
      .pipe(
        map((jsonLastOpenedScreenDateByChannels: JsonChannelLastOpenedDate[]) =>
          jsonLastOpenedScreenDateByChannels.map(
            (jsonLastOpenedScreenDateByChannel: JsonChannelLastOpenedDate) => new ChannelLastOpenedDate(jsonLastOpenedScreenDateByChannel)
          )
        )
      )
      .pipe(catchError((error: Error) => throwError(error)));
  }

  /**
   * Get abattements for each campaign
   *
   * @param campaignIds (param)
   */
  getAbattementsByCampaign(campaignIds: string): Observable<CampaignAbattement[]> {
    const apiBaseUrl = `${this.routeAbattementCampaigns}`;
    const params: HttpParams = this.buildParamsApi({
      campaign_ids: campaignIds,
    });

    return this.http
      .get(apiBaseUrl, { params })
      .pipe(
        map((jsonCampaignsAbatts: JsonCampaignAbattement[]) =>
          jsonCampaignsAbatts.map((jsonCampaignAbatt: JsonCampaignAbattement) => new CampaignAbattement(jsonCampaignAbatt))
        )
      )
      .pipe(catchError((error: Error) => throwError(error)));
  }

  /**
   * Get product sector
   *
   * @param filters
   */
  getProductSector(filters: object): Observable<string> {
    const apiBaseUrl = `${this.routeProductSector}`;
    const params: HttpParams = this.buildParamsApi(filters);

    return this.http
      .get(apiBaseUrl, { params })
      .pipe(map((productSector: string) => productSector))
      .pipe(catchError((error: Error) => throwError(error)));
  }

  /**
   * Event when we need to reset
   */
  changeLoadingState(state: boolean): void {
    this.isLoading.next(state);
  }

  /**
   * Event when select/unselect screen
   */
  eventUpdateTotalScreen(data: { screen: Screen; mode: boolean }): void {
    this.updateTotalScreen.next(data);
  }

  /**
   * Event when create new screen
   */
  eventScreenHourChange(newHour): void {
    this.updateScreenHour.next(newHour);
  }

  /**
   * Transform filters into params
   * @param filters
   */
  buildParamsApi(filters: object): HttpParams {
    let params = new HttpParams();

    if (filters && typeof filters === 'object') {
      Object.entries(filters).forEach(item => {
        params = params.append(item[0], item[1]);
      });
    }

    return params;
  }

  public getScreensForBroadcastIncident(filter): Observable<BroadcastIncidentScreen[]> {
    let httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Écrans non trouvés.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];
    let link = environment.api_base_url + '/tv/get-screens-for-broadcast-incidents' + '?' + this.getParamsFromFilter(filter);

    return new Observable(observer => {
      this.http
        .get(link)
        .pipe(
          map((response: any) => {
            if (response && response.length > 0) {
              return response.map(screen => new BroadcastIncidentScreen(screen));
            }
            return response;
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  public toggleBroadcastIncidentStateOnScreens(screens: BroadcastIncidentScreen[], comment, shouldCancelBroadcastIncidentState: boolean) {
    let httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: 'Une erreur est survenue lors de la mise en ID.',
      },
      {
        statusCode: 422,
        message: 'Ecran facturé, annulation impossible',
      },
    ];

    let link = environment.api_base_url + '/tv/toggle-broadcast-incident-state-on-screens';

    const now = new Date();
    const fileName = 'Flash_Special_' + now.getTime() + '.xlsx';
    const reportName = 'Report_ID' + now.getTime() + '.csv';

    const filter = {
      fileName: fileName,
      reportName: reportName,
    };

    link += '?' + this.getParamsFromFilter(filter);

    const preparedScreens = screens.map(screen => {
      return {
        breakCode: screen.breakCode,
        broadcastDate: moment(screen.broadcastDate).format('YYYY-MM-DD'),
        channelCode: screen.channelCode,
        regionCode: screen.regionCode,
        isRegion: screen.isRegion ? 'Y' : 'N',
        userId: this.authService.currentUserData ? this.authService.currentUserData.sub : '',
        comment: comment,
      };
    });

    const data = {
      shouldCancelBroadcastIncidentState: shouldCancelBroadcastIncidentState,
      screens: preparedScreens,
    };

    return new Observable(observer => {
      this.http.patch(link, data).subscribe(
        () => {
          this.downloadSpecialIdFlash(fileName, observer);
          this.downloadSpecialIdReport(reportName, observer);
        },
        error => {
          if (error.status === 504 || error.status === 0) {
            this.downloadSpecialIdFlash(fileName, observer);
            this.downloadSpecialIdReport(reportName, observer);
          } else {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        }
      );
    });
  }

  public downloadSpecialIdFlash(fileName: string, observer) {
    let httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: 'Une erreur est survenue lors du téléchargement du flash.',
      },
      {
        statusCode: 404,
        message: 'Fichier : ' + fileName + ' non trouvé.',
      },
    ];
    let url = environment.api_base_url + '/tv/download-special-id-flash';
    const filter = {
      fileName: fileName,
    };

    url += '?' + this.getParamsFromFilter(filter);

    this.http
      .get(url, { reportProgress: true, responseType: 'blob' })
      .pipe(
        map(res =>
          this.downloadFile(
            new FileModel({
              fileName: fileName,
              data: res,
            })
          )
        )
      )
      .subscribe(
        (response: any) => observer.next(response),
        error => {
          if (error.status === 504 || error.status === 0 || error.status === 404) {
            this.downloadSpecialIdFlash(fileName, observer);
          } else {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        }
      );
  }

  public downloadSpecialIdReport(reportName: string, observer) {
    let httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: 'Une erreur est survenue lors du téléchargement du recap spot en ID.',
      },
      {
        statusCode: 404,
        message: 'Fichier : ' + reportName + ' non trouvé.',
      },
    ];
    let url = environment.api_base_url + '/tv/download-special-id-report';
    const filter = {
      reportName: reportName,
    };

    url += '?' + this.getParamsFromFilter(filter);

    this.http
      .get(url, { reportProgress: true, responseType: 'blob' })
      .pipe(
        map(res =>
          this.downloadFile(
            new FileModel({
              fileName: reportName,
              data: res,
            })
          )
        )
      )
      .subscribe(
        (response: any) => observer.next(response),
        error => {
          if (error.status === 504 || error.status === 0 || error.status === 404) {
            this.downloadSpecialIdReport(reportName, observer);
          } else {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        }
      );
  }

  public exportScreenGrid(filters): Observable<void> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: "Une erreur est survenue lors de l'export de la grille.",
      },
    ];
    const apiUrl = `${this.routeExportScreenGrid}`;

    return new Observable<void>(observer => {
      this.http.post(apiUrl, filters, { reportProgress: true, responseType: 'blob' }).subscribe(
        () => {
          return this.getFile(filters.fileName, observer);
        },
        error => {
          // This check error.status == 0 is for r7 because 504 gives 0 in error object in R7 environment
          if (error.status === 504 || error.status === 0) {
            return this.getFile(filters.fileName, observer);
          } else {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        }
      );
    });
  }

  public exportTypeWeek(filters): Observable<void> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: "Une erreur est survenue lors de l'export de la semaine type.",
      },
    ];
    const apiUrl = `${this.routeExportTypeWeek}`;

    return new Observable<void>(observer => {
      this.http.post(apiUrl, filters, { reportProgress: true, responseType: 'blob' }).subscribe(
        () => {
          return this.getFile(filters.fileName, observer);
        },
        error => {
          // This check error.status == 0 is for r7 because 504 gives 0 in error object in R7 environment
          if (error.status === 504 || error.status === 0) {
            return this.getFile(filters.fileName, observer);
          } else {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        }
      );
    });
  }

  public getFile(fileName: string, observer) {
    let httpErrors: HttpError[] = [
      {
        statusCode: 500,
        message: "Une erreur est survenue lors du téléchargement de l'export.",
      },
      {
        statusCode: 404,
        message: 'Fichier : ' + fileName + ' non trouvé.',
      },
    ];
    const params = new URLSearchParams();
    params.set('file_name', fileName);

    const apiUrl = `${this.routeDownloadScreenGrid}` + '?' + params.toString();

    this.http
      .get(apiUrl, { reportProgress: true, responseType: 'blob' })
      .pipe(
        map(res =>
          this.downloadFile(
            new FileModel({
              fileName: fileName,
              data: res,
            })
          )
        )
      )
      .subscribe(
        (response: any) => observer.next(response),
        error => {
          if (error.status === 504 || error.status === 0 || error.status === 404) {
            this.getFile(fileName, observer);
          } else {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        }
      );
  }
}
